import { apiRequest } from './apiClient'
import { getValidToken, hasRefreshableSession } from './authService'
import { hasNative, nativePlatform, native } from './nativeBridge'

/**
 * Web-push: подписка браузера на уведомления колокольчика.
 *
 * Подписка живёт в паре (браузер, origin) и НЕ переносится между устройствами:
 * человек, включивший пуши на телефоне, на рабочем ПК включает их отдельно.
 * Под WL то же самое произойдёт на партнёрском домене — так устроен Push API,
 * это не наше ограничение.
 */

function withAuth(options = {}) {
  const token = getValidToken()
  if (!token) {
    // Токен в памяти протух (вкладка висела в фоне дольше TTL access-токена).
    // Это не разлогин: refresh-cookie жива, apiClient сам сделает silentRefresh
    // и подставит свежий токен. Падаем только когда сессии нет вообще.
    if (hasRefreshableSession()) return options
    throw new Error('token_required')
  }

  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  }
}

/**
 * Состояния, в которых может быть тумблер. Их пять, и все нужно показать
 * по-разному, иначе поддержка утонет в «у меня не работает».
 */
export const PUSH_STATE = {
  UNSUPPORTED: 'unsupported', // браузер не умеет push (или это iOS во вкладке)
  IOS_NEEDS_INSTALL: 'ios_needs_install', // Safari умеет, но только из home screen
  DENIED: 'denied', // человек запретил — тумблером уже не починить
  OFF: 'off', // можно включить
  ON: 'on', // подписан
}

function isIos() {
  return /iP(hone|ad|od)/.test(navigator.userAgent)
}

function isStandalone() {
  // iOS отдаёт navigator.standalone, остальные — display-mode
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches
}

/**
 * Приложения iOS и Android. Там не Push API браузера, а APNs и FCM: токен
 * выдаёт оболочка, сервер хранит его в той же push_subscription с endpoint'ом
 * apns:<среда>:<токен> или fcm:<токен> — строки те же, что строят
 * ApnsSender.php и FcmSender.php. Состояния и список устройств при этом общие,
 * страница настроек не знает, через какой канал идёт доставка.
 */
const isAndroidApp = () => nativePlatform() === 'android'
const nativeEndpoint = (r) => (isAndroidApp() ? `fcm:${r.token}` : `apns:${r.env}:${r.token}`)

async function nativePushState() {
  const s = await native.pushStatus()
  if (!s) return { state: PUSH_STATE.UNSUPPORTED, endpoint: null }
  if (s.status === 'denied') return { state: PUSH_STATE.DENIED, endpoint: null }
  if (s.status === 'authorized' && s.token) {
    return { state: PUSH_STATE.ON, endpoint: nativeEndpoint(s) }
  }
  return { state: PUSH_STATE.OFF, endpoint: null }
}

/**
 * Текущее состояние подписки в ЭТОМ браузере (или в приложении).
 * @returns {Promise<{state: string, endpoint: string|null}>}
 */
export async function getPushState() {
  if (hasNative()) return nativePushState()

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // На iOS до 16.4 и в обычной вкладке Safari PushManager отсутствует.
    // Различаем причины, чтобы дать полезную подсказку, а не «не поддерживается».
    if (isIos() && !isStandalone()) {
      return { state: PUSH_STATE.IOS_NEEDS_INSTALL, endpoint: null }
    }
    return { state: PUSH_STATE.UNSUPPORTED, endpoint: null }
  }

  if (Notification.permission === 'denied') {
    return { state: PUSH_STATE.DENIED, endpoint: null }
  }

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()

  return subscription
    ? { state: PUSH_STATE.ON, endpoint: subscription.endpoint }
    : { state: PUSH_STATE.OFF, endpoint: null }
}

/**
 * Настройки пушей с сервера. В приложении «включено» означает ключ своего
 * канала (APNs или FCM), а не VAPID: браузерные пуши могут работать, а
 * приложение ещё ждать ключа.
 */
export async function fetchPushConfig() {
  const config = await apiRequest('/push/config', withAuth())
  if (!hasNative()) return config
  return { ...config, enabled: Boolean(isAndroidApp() ? config?.fcmEnabled : config?.apnsEnabled) }
}

/**
 * VAPID-ключ приходит как base64url-строка, а pushManager.subscribe требует
 * Uint8Array — иначе получим InvalidCharacterError.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)

  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

/**
 * Запрашивает разрешение и подписывает браузер.
 * Вызывать ТОЛЬКО по клику: без жеста пользователя Chrome не покажет запрос,
 * а за навязчивые запросы ещё и понижает репутацию сайта.
 *
 * @returns {Promise<{state: string, endpoint?: string}>}
 */
export async function enablePush() {
  if (hasNative()) return enableNativePush()

  const { state } = await getPushState()
  if (state === PUSH_STATE.UNSUPPORTED || state === PUSH_STATE.IOS_NEEDS_INSTALL) {
    return { state }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { state: permission === 'denied' ? PUSH_STATE.DENIED : PUSH_STATE.OFF }
  }

  const config = await fetchPushConfig()
  if (!config?.enabled || !config?.publicKey) {
    throw new Error('push_not_configured')
  }

  const registration = await navigator.serviceWorker.ready

  // Могла остаться подписка от прошлого ключа — она уже не расшифруется,
  // поэтому переподписываемся, а не переиспользуем.
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    await existing.unsubscribe().catch(() => {})
  }

  const subscription = await registration.pushManager.subscribe({
    // Без этого флага Chrome не подпишет: молчаливые пуши запрещены.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.publicKey),
  })

  const raw = subscription.toJSON()
  await apiRequest('/push/subscribe', withAuth({
    method: 'POST',
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      p256dh: raw.keys?.p256dh,
      auth: raw.keys?.auth,
    }),
  }))

  return { state: PUSH_STATE.ON, endpoint: subscription.endpoint }
}

async function enableNativePush() {
  const config = await fetchPushConfig()
  if (!config?.enabled) {
    throw new Error('push_not_configured')
  }

  // Здесь телефон может показать системный запрос разрешения — поэтому только по клику.
  const r = await native.pushToken()
  if (!r) {
    const { state } = await nativePushState()
    return { state: state === PUSH_STATE.DENIED ? PUSH_STATE.DENIED : PUSH_STATE.OFF }
  }

  // Android: токен FCM, среды у него нет. iOS: токен APNs со средой сборки.
  await apiRequest(isAndroidApp() ? '/push/fcm' : '/push/apns', withAuth({
    method: 'POST',
    body: JSON.stringify(isAndroidApp()
      ? { token: r.token, device: r.device }
      : { token: r.token, env: r.env, device: r.device }),
  }))

  return { state: PUSH_STATE.ON, endpoint: nativeEndpoint(r) }
}

/** Отписывает ТЕКУЩИЙ браузер и удаляет его запись на сервере. */
export async function disablePush() {
  if (hasNative()) {
    // Разрешение в телефоне остаётся — его человек снимает в настройках.
    // Нам достаточно убрать запись на сервере: без неё крон сюда не шлёт.
    const { endpoint } = await nativePushState()
    if (endpoint) {
      await apiRequest('/push/unsubscribe', withAuth({
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      })).catch(() => {})
    }
    return { state: PUSH_STATE.OFF }
  }

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()

  if (subscription) {
    // Сначала сервер: если браузер отпишется, а запрос не уйдёт, на сервере
    // останется мёртвая подписка, в которую крон будет долбиться до 410.
    await apiRequest('/push/unsubscribe', withAuth({
      method: 'POST',
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    })).catch(() => {})

    await subscription.unsubscribe().catch(() => {})
  }

  return { state: PUSH_STATE.OFF }
}

/**
 * SHA-256 endpoint'а текущего браузера, первые 16 символов — тем же способом,
 * что считает сервер. Нужно, чтобы опознать свою строку в списке устройств:
 * при её удалении надо снять подписку и в самом браузере, иначе на сервере
 * записи нет, а браузер считает себя подписанным — и кнопка «підписати» не
 * появится, потому что состояние разъедется.
 */
export async function getCurrentEndpointHash() {
  if (hasNative()) {
    const { endpoint } = await nativePushState()
    return endpoint ? endpointHash(endpoint) : null
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null
  }

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    return null
  }

  return endpointHash(subscription.endpoint)
}

async function endpointHash(endpoint) {
  const bytes = new TextEncoder().encode(endpoint)
  const digest = await crypto.subtle.digest('SHA-256', bytes)

  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16)
}

/** Список своих подписанных устройств (для карточки в настройках). */
export async function fetchPushDevices() {
  return apiRequest('/push/devices', withAuth())
}

/** Отписать чужое устройство из списка (например, потерянный телефон). */
export async function removePushDevice(id) {
  return apiRequest(`/push/devices/${id}`, withAuth({ method: 'DELETE' }))
}

/**
 * Тестовый пуш на КОНКРЕТНОЕ устройство из списка.
 *
 * Именно по id, а не «на текущий браузер»: у человека может быть два одинаковых
 * «iPhone · Safari», и понять, который из них ещё живой, можно только послав
 * на каждый отдельно. Заодно это позволяет проверить телефон, сидя за десктопом.
 */
export async function sendTestPush(subscriptionId) {
  return apiRequest('/push/test', withAuth({
    method: 'POST',
    body: JSON.stringify({ id: subscriptionId }),
  }))
}
